import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Briefcase, CheckCircle2, Clock, ChevronRight, Search, Mic, Lock } from "lucide-react";

const ScenariosPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ total: 0, completed: 0, by_subject: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/scenarios");
        setData(res);
      } catch (e) {
        console.error("Scenarios load failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading clinical cases...</p>
        </div>
      </div>
    );
  }

  const filteredGroups = data.by_subject
    .map((g) => ({
      ...g,
      scenarios: g.scenarios.filter((s) =>
        s.title.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter((g) => g.scenarios.length > 0);

  const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;
  const locked = !!data.locked;
  const openCase = (id) => navigate(locked ? "/billing" : `/cases/${id}`);
  const openRolePlay = (id) => navigate(locked ? "/billing" : `/cases/${id}/role-play`);

  return (
    <div data-testid="scenarios-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
          <Briefcase className="w-8 h-8 text-primary" />
          Clinical Cases
        </h1>
        <p className="text-muted-foreground mt-1">
          Real exam scenarios. Read the case, learn how to manage it, then test yourself.
        </p>
      </div>

      {/* Progress */}
      <div className="glass rounded-2xl p-5 border border-border flex items-center gap-5">
        <div className="w-14 h-14 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
          <Briefcase className="w-7 h-7 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">Cases completed</span>
            <span className="text-sm text-muted-foreground">
              {data.completed} / {data.total}
            </span>
          </div>
          <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Each case takes ~10–15 minutes. Aim for 2 cases per day.
          </p>
        </div>
      </div>

      {locked && (
        <div className="rounded-2xl p-5 border border-warning/40 bg-warning/5 flex items-center gap-4" data-testid="osce-locked-banner">
          <div className="w-11 h-11 rounded-xl bg-warning/15 text-warning flex items-center justify-center flex-shrink-0">
            <Lock className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-sm">OSCE cases are a Pro feature</p>
            <p className="text-sm text-muted-foreground">Unlock every clinical case and voice role-play.</p>
          </div>
          <button
            onClick={() => navigate("/billing")}
            data-testid="osce-upgrade-btn"
            className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium whitespace-nowrap"
          >
            Upgrade to unlock OSCE cases
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          data-testid="scenarios-search"
          placeholder="Search cases..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-11 pr-4 py-3 rounded-full bg-secondary border border-border text-sm focus:outline-none focus:border-primary/50"
        />
      </div>

      {/* Grouped list */}
      {filteredGroups.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          No cases found. Try a different search.
        </div>
      ) : (
        filteredGroups.map((group) => (
          <div key={group.subject_id}>
            <div className="flex items-center gap-2 mb-3">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: group.subject_color }}
              />
              <h2 className="text-base font-semibold">{group.subject_name}</h2>
              <span className="text-xs text-muted-foreground">
                {group.scenarios.length} {group.scenarios.length === 1 ? "case" : "cases"}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {group.scenarios.map((sc) => (
                <div
                  key={sc.id}
                  data-testid={`scenario-card-${sc.id}`}
                  className="text-left p-5 rounded-2xl glass border border-border hover:border-primary/40 hover-lift transition-all group relative"
                >
                  <button
                    onClick={() => openCase(sc.id)}
                    className="block w-full text-left"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"
                        style={{
                          backgroundColor: `${sc.subject_color}20`,
                          color: sc.subject_color,
                        }}
                      >
                        {sc.subject_name}
                      </span>
                      {locked ? (
                        <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" data-testid={`scenario-lock-${sc.id}`} />
                      ) : sc.completed && (
                        <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                      )}
                    </div>
                    <p className="font-medium text-sm leading-snug mb-3 pr-2">{sc.title}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {sc.estimated_time} min
                      </span>
                      <ChevronRight className="w-4 h-4 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </button>

                  {/* Quick "Talk to AI patient" pill */}
                  <button
                    data-testid={`scenario-roleplay-quick-${sc.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openRolePlay(sc.id);
                    }}
                    title="Practice with a virtual patient"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2 rounded-full bg-warning/10 hover:bg-warning/20 text-warning text-xs font-medium border border-warning/30 transition-all"
                  >
                    <Mic className="w-3.5 h-3.5" />
                    Talk to patient
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ScenariosPage;
